import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Bot, 
  User,
  Clock,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Filter,
  TrendingUp,
  Calendar,
  Target,
  Zap,
  Search,
  AlertCircle,
  ArrowRight,
  History,
  BarChart3,
  Users,
  Activity,
  Archive,
  Pencil,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ArchiveConfirmDialog from '@/components/common/ArchiveConfirmDialog';
import BotClarificationChatModal from '@/components/bots/BotClarificationChatModal';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow, differenceInHours, isPast, subDays, isToday, isFuture } from 'date-fns';

// Sub-components
import TaskDiagnosticsPanel from './TaskDiagnosticsPanel';
import TaskHistoryTimeline from './TaskHistoryTimeline';
import TaskQuickAdd from './TaskQuickAdd';

export default function ActivityTaskLogView({ 
  activity, 
  tasks, 
  officers, 
  issues,
  progressReports,
  onAddTask, 
  onEditTask,
  onReportIssue,
  currentUserEmail
}) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('current');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [botChatModalOpen, setBotChatModalOpen] = useState(false);
  const [selectedBotTask, setSelectedBotTask] = useState(null);


  // Mutations
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, ...updates }) => base44.entities.DailyTask.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  // Computed Data
  const tasksByStatus = useMemo(() => {
    return tasks.reduce((acc, task) => {
      if (task.status_flag === 'green') {
        acc.conquered.push(task);
      } else if (task.status_flag === 'red') {
        acc.struggling.push(task);
      } else if (task.status_flag === 'yellow') {
        acc.imminent.push(task);
      } else {
        acc.notStarted.push(task);
      }
      return acc;
    }, { conquered: [], imminent: [], struggling: [], notStarted: [] });
  }, [tasks]);

  const stats = {
    conquered: tasksByStatus.conquered.length,
    imminent: tasksByStatus.imminent.length,
    struggling: tasksByStatus.struggling.length,
    notStarted: tasksByStatus.notStarted.length,
    total: tasks.length
  };

  // Diagnostics
  const diagnostics = useMemo(() => {
    const now = new Date();
    const draggingThresholdHours = 24;
    
    return {
      unstarted: tasks.filter(t => t.status_flag !== 'green' && !t.started_at && t.status_flag !== 'red'),
      dragging: tasks.filter(t => {
        if (t.status_flag === 'green' || t.status_flag !== 'yellow' || !t.started_at) return false;
        const hoursInProgress = differenceInHours(now, new Date(t.started_at));
        return hoursInProgress > draggingThresholdHours;
      }),
      overdue: tasks.filter(t => t.status_flag !== 'green' && t.date && isPast(new Date(t.date + 'T00:00:00')) && !isToday(new Date(t.date + 'T00:00:00'))),
      noKRLink: tasks.filter(t => t.status_flag !== 'green' && !t.key_result_focus),
      struggling: tasksByStatus.struggling,
      conflicting: tasks.filter(t => t.status_flag !== 'green' && t.ai_assessment?.type === 'overlap'),
      redundant: tasks.filter(t => t.status_flag !== 'green' && t.ai_assessment?.type === 'redundant')
    };
  }, [tasks, tasksByStatus]);

  const hasDiagnosticIssues = Object.values(diagnostics).some(arr => arr.length > 0);

  // Task history (completed tasks grouped by date)
  const taskHistory = useMemo(() => {
    const completed = tasks.filter(t => t.completed);
    const grouped = completed.reduce((acc, task) => {
      const dateKey = task.completed_at ? format(new Date(task.completed_at), 'yyyy-MM-dd') : 'unknown';
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(task);
      return acc;
    }, {});
    return Object.entries(grouped)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 14); // Last 14 days
  }, [tasks]);

  // KR Impact calculation
  const krImpact = useMemo(() => {
    if (!activity.key_results || activity.key_results.length === 0) return [];
    
    return activity.key_results.map(kr => {
      const linkedTasks = tasks.filter(t => t.key_result_focus === kr.description);
      const completedLinked = linkedTasks.filter(t => t.completed);
      return {
        ...kr,
        totalTasks: linkedTasks.length,
        completedTasks: completedLinked.length,
        progress: kr.target ? (parseFloat(kr.current_value || 0) / parseFloat(kr.target)) * 100 : 0
      };
    });
  }, [activity.key_results, tasks]);

  // Officer workload
  const officerWorkload = useMemo(() => {
    const workload = {};
    tasks.forEach(task => {
      if (!workload[task.user_email]) {
        const officer = officers.find(o => o.user_email === task.user_email);
        workload[task.user_email] = {
          officer,
          total: 0,
          completed: 0,
          struggling: 0,
          active: 0
        };
      }
      workload[task.user_email].total++;
      if (task.completed) workload[task.user_email].completed++;
      else if (task.status_flag === 'red') workload[task.user_email].struggling++;
      else if (task.started_at) workload[task.user_email].active++;
    });
    return Object.values(workload);
  }, [tasks, officers]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(t => !t.completed && !t.archived);
    if (statusFilter === 'struggling') filtered = tasksByStatus.struggling.filter(t => !t.archived);
    else if (statusFilter === 'imminent') filtered = tasksByStatus.imminent.filter(t => !t.archived);
    else if (statusFilter === 'notStarted') filtered = tasksByStatus.notStarted.filter(t => !t.archived);
    return filtered.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [tasks, statusFilter, tasksByStatus]);

  const archivedTasks = useMemo(() => {
    return tasks.filter(t => t.archived).sort((a, b) => new Date(b.archived_at || 0) - new Date(a.archived_at || 0));
  }, [tasks]);

  const getOfficer = (email) => officers.find(o => o.user_email === email);

  const isAccountable = activity.accountable_email === currentUserEmail;
  const isResponsible = activity.responsible_email === currentUserEmail;
  const canManageTasks = isAccountable || isResponsible;

  // Handle running a bot task - opens chat modal
  const handleRunBotTask = (task) => {
    setSelectedBotTask(task);
    setBotChatModalOpen(true);
  };

  const handleBotChatComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
  };

  // Handle history item click - navigate to appropriate tab and highlight
  const handleHistoryItemClick = (item) => {
    // Clear highlight after delay
    const clearHighlight = () => {
      setTimeout(() => setHighlightedItemId(null), 3000);
    };

    if (item.type === 'task_created' || item.type === 'task_started' || item.type === 'task_blocked') {
      // Navigate to Active tab and highlight task
      setActiveTab('current');
      setStatusFilter('all');
      setExpandedTaskId(item.data.id);
      setHighlightedItemId(item.data.id);
      clearHighlight();
      
      // Scroll to element after tab switch
      setTimeout(() => {
        const element = document.getElementById(`task-${item.data.id}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (item.type === 'task_completed') {
      // For completed tasks, expand it in current view
      setHighlightedItemId(item.data.id);
      clearHighlight();
    } else if (item.type === 'officer_joined') {
      // Navigate to Officers tab
      setActiveTab('officers');
      setHighlightedItemId(item.data.officer_email);
      clearHighlight();
      
      setTimeout(() => {
        const element = document.getElementById(`officer-${item.data.officer_email}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (item.type === 'kr_updated') {
      // Navigate to KR Impact tab
      setActiveTab('krs');
      setHighlightedItemId(`kr-${item.data.description}`);
      clearHighlight();
      
      setTimeout(() => {
        const element = document.getElementById(`kr-${item.data.description}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (item.type === 'issue_reported') {
      // Highlight the issue - could navigate to issues section if available
      setHighlightedItemId(item.data.id);
      clearHighlight();
    } else if (item.type === 'progress_report') {
      // Could open report detail modal
      setHighlightedItemId(item.data.id);
      clearHighlight();
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header Stats Bar */}
      <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-800/60 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4 text-xs font-mono flex-wrap">
            <div className="flex items-center gap-1.5 text-emerald-400">
              <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_currentColor]" />
              <span className="font-bold">{stats.conquered}</span>
              <span className="opacity-60 text-[10px] uppercase hidden sm:inline">Conquered</span>
            </div>
            <div className="flex items-center gap-1.5 text-amber-400">
              <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_8px_currentColor]" />
              <span className="font-bold">{stats.imminent}</span>
              <span className="opacity-60 text-[10px] uppercase hidden sm:inline">Imminently Conquering</span>
            </div>
            <div className="flex items-center gap-1.5 text-rose-400">
              <motion.div 
                className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_currentColor]" 
                animate={{ opacity: stats.struggling > 0 ? [1, 0.4, 1] : 1 }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="font-bold">{stats.struggling}</span>
              <span className="opacity-60 text-[10px] uppercase hidden sm:inline">Struggling</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <div className="w-2 h-2 rounded-full bg-slate-500" />
              <span className="font-bold">{stats.notStarted}</span>
              <span className="opacity-60 text-[10px] uppercase hidden sm:inline">Queued</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasDiagnosticIssues && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDiagnostics(!showDiagnostics)}
                      className={cn(
                        "h-7 px-2 text-xs",
                        showDiagnostics ? "bg-amber-500/20 text-amber-400" : "text-amber-400 hover:bg-amber-500/10"
                      )}
                    >
                      <AlertCircle className="w-3.5 h-3.5 mr-1" />
                      <span className="hidden sm:inline">Diagnostics</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Run task diagnostics</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {canManageTasks && (
              <Button
                size="sm"
                onClick={onAddTask}
                className="h-7 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                <span className="hidden sm:inline">Add Task</span>
              </Button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 w-full flex bg-slate-800/50 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }} 
            animate={{ width: `${(stats.conquered / Math.max(stats.total, 1)) * 100}%` }} 
            className="bg-emerald-500 h-full" 
          />
          <motion.div 
            initial={{ width: 0 }} 
            animate={{ width: `${(stats.imminent / Math.max(stats.total, 1)) * 100}%` }} 
            className="bg-amber-500 h-full" 
          />
          <motion.div 
            initial={{ width: 0 }} 
            animate={{ width: `${(stats.struggling / Math.max(stats.total, 1)) * 100}%` }} 
            className="bg-rose-500 h-full" 
          />
        </div>
      </div>

      {/* Diagnostics Panel (Collapsible) */}
      <AnimatePresence>
        {showDiagnostics && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-4"
          >
            <TaskDiagnosticsPanel 
              diagnostics={diagnostics} 
              officers={officers}
              onClose={() => setShowDiagnostics(false)}
              onTaskClick={(task) => {
                setShowDiagnostics(false);
                setActiveTab('current');
                setStatusFilter('all');
                setExpandedTaskId(task.id);
                setHighlightedItemId(task.id);
                setTimeout(() => setHighlightedItemId(null), 3000);
                setTimeout(() => {
                  const element = document.getElementById(`task-${task.id}`);
                  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="bg-slate-900/50 border border-slate-800 p-1 h-auto mb-3">
          <TabsTrigger value="current" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Activity className="w-3.5 h-3.5 mr-1.5" />
            Active ({filteredTasks.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <History className="w-3.5 h-3.5 mr-1.5" />
            History ({tasksByStatus.conquered.length})
          </TabsTrigger>
          <TabsTrigger value="archived" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Archive className="w-3.5 h-3.5 mr-1.5" />
            Archived ({archivedTasks.length})
          </TabsTrigger>
          <TabsTrigger value="officers" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            Officers ({officerWorkload.length})
          </TabsTrigger>
          <TabsTrigger value="krs" className="text-xs data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Target className="w-3.5 h-3.5 mr-1.5" />
            KR Impact
          </TabsTrigger>
        </TabsList>

        {/* Current Tasks Tab */}
        <TabsContent value="current" className="flex-1 mt-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-7 w-[140px] text-[10px] border-slate-700 bg-slate-900/80 text-slate-300 uppercase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1221] border-slate-800 text-slate-300">
                <SelectItem value="all">All Active</SelectItem>
                <SelectItem value="imminent">Imminently Conquering</SelectItem>
                <SelectItem value="struggling">Struggling</SelectItem>
                <SelectItem value="notStarted">Not Started</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="h-[250px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full">
            <div className="space-y-2">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg text-xs text-slate-600">
                  No tasks found matching filter
                </div>
              ) : (
                filteredTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    id={`task-${task.id}`}
                    task={task}
                    officer={getOfficer(task.user_email)}
                    officers={officers}
                    isExpanded={expandedTaskId === task.id}
                    isHighlighted={highlightedItemId === task.id}
                    onToggleExpand={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                    onUpdate={(updates) => updateTaskMutation.mutate({ id: task.id, ...updates })}
                    onReportIssue={() => onReportIssue?.(task)}
                    canManage={canManageTasks}
                    currentUserEmail={currentUserEmail}
                    onEdit={() => onEditTask?.(task)}
                    onRunBotTask={handleRunBotTask}
                  />
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="flex-1 mt-0 overflow-hidden">
          <ScrollArea className="h-[250px]">
            <TaskHistoryTimeline 
              taskHistory={taskHistory}
              allTasks={tasks}
              officers={officers}
              issues={issues}
              progressReports={progressReports}
              activity={activity}
              onItemClick={handleHistoryItemClick}
            />
          </ScrollArea>
        </TabsContent>

        {/* Archived Tab */}
        <TabsContent value="archived" className="flex-1 mt-0 overflow-hidden">
          <div className="h-[250px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full">
            <div className="space-y-2">
              {archivedTasks.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg text-xs text-slate-600">
                  No archived tasks
                </div>
              ) : (
                archivedTasks.map(task => {
                  const officer = getOfficer(task.user_email);
                  const isBot = officer?.is_bot || officer?.custom_role === 'AI Officer';
                  
                  return (
                    <div 
                      key={task.id}
                      className="relative rounded border border-slate-800 bg-slate-900/30 p-3 opacity-60"
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-600" />
                      
                      <div className="pl-3">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-slate-600 text-slate-300 text-[9px]">ARCHIVED</Badge>
                              {task.archived_at && (
                                <span className="text-[9px] text-slate-500 font-mono">
                                  {format(new Date(task.archived_at), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                            <h4 className="text-sm text-slate-400 line-through leading-snug">
                              {task.task_description}
                            </h4>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center shrink-0">
                            {officer?.avatar_url ? (
                              <img src={officer.avatar_url} className="w-full h-full object-cover" alt="" />
                            ) : isBot ? (
                              <Bot className="w-3 h-3 text-slate-500" />
                            ) : (
                              <span className="text-[10px] text-slate-500">
                                {officer?.callsign?.substring(0, 2)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {canManageTasks && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] bg-indigo-900/20 border-indigo-800/50 text-indigo-400 hover:bg-indigo-900/40 mt-2"
                            onClick={() => {
                              updateTaskMutation.mutate({
                                id: task.id,
                                archived: false,
                                archived_at: null
                              });
                            }}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> Unarchive
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </TabsContent>

        {/* Officers Tab */}
        <TabsContent value="officers" className="flex-1 mt-0 overflow-hidden">
          <ScrollArea className="h-[250px]">
            <div className="space-y-3 pr-2">
              {officerWorkload.map(({ officer, total, completed, struggling, active }) => (
                <div 
                  key={officer?.user_email || 'unknown'}
                  id={`officer-${officer?.user_email}`}
                  className={cn(
                    "bg-slate-900/50 p-3 rounded-lg border transition-all",
                    highlightedItemId === officer?.user_email 
                      ? "border-cyan-500 ring-2 ring-cyan-500/30" 
                      : "border-slate-800"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
                      {officer?.avatar_url ? (
                        <img src={officer.avatar_url} className="w-full h-full object-cover" alt="" />
                      ) : officer?.is_bot ? (
                        <Bot className="w-4 h-4 text-purple-400" />
                      ) : (
                        <User className="w-4 h-4 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-200">{officer?.callsign || 'Unknown'}</div>
                      <div className="text-[10px] text-slate-500">{officer?.rank_title || 'Officer'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-slate-200">{total}</div>
                      <div className="text-[10px] text-slate-500">tasks</div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                      <div className="bg-emerald-500 h-full" style={{ width: `${(completed / Math.max(total, 1)) * 100}%` }} />
                      <div className="bg-amber-500 h-full" style={{ width: `${(active / Math.max(total, 1)) * 100}%` }} />
                      <div className="bg-rose-500 h-full" style={{ width: `${(struggling / Math.max(total, 1)) * 100}%` }} />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-2 text-[10px]">
                    <span className="text-emerald-400">{completed} done</span>
                    <span className="text-amber-400">{active} active</span>
                    <span className="text-rose-400">{struggling} blocked</span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* KR Impact Tab */}
        <TabsContent value="krs" className="flex-1 mt-0 overflow-hidden">
          <ScrollArea className="h-[250px]">
            <div className="space-y-3 pr-2">
              {krImpact.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg text-xs text-slate-600">
                  No key results defined
                </div>
              ) : (
                krImpact.map((kr, idx) => (
                  <div 
                    key={idx}
                    id={`kr-${kr.description}`}
                    className={cn(
                      "bg-slate-900/50 p-3 rounded-lg border transition-all",
                      highlightedItemId === `kr-${kr.description}` 
                        ? "border-purple-500 ring-2 ring-purple-500/30" 
                        : "border-slate-800"
                    )}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <p className="text-sm text-slate-200 flex-1">{kr.description}</p>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-emerald-400">
                          {kr.current_value || 0}/{kr.target}
                        </div>
                        <div className="text-[10px] text-slate-500">{kr.unit || ''}</div>
                      </div>
                    </div>
                    
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-2">
                      <motion.div 
                        className={cn(
                          "h-full",
                          kr.progress >= 70 ? 'bg-emerald-500' : kr.progress >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                        )}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(kr.progress, 100)}%` }}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-slate-500">
                        {kr.totalTasks} tasks linked ({kr.completedTasks} completed)
                      </span>
                      <span className={cn(
                        "font-mono",
                        kr.progress >= 70 ? 'text-emerald-400' : kr.progress >= 40 ? 'text-amber-400' : 'text-rose-400'
                      )}>
                        {Math.round(kr.progress)}%
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Bot Chat Modal */}
      <BotClarificationChatModal
        open={botChatModalOpen}
        onOpenChange={setBotChatModalOpen}
        task={selectedBotTask}
        onExecutionComplete={handleBotChatComplete}
      />
    </div>
  );
}

// Task Card Component
function TaskCard({ id, task, officer, officers, isExpanded, isHighlighted, onToggleExpand, onUpdate, onReportIssue, canManage, currentUserEmail, onEdit, onRunBotTask }) {
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const isBot = officer?.is_bot || officer?.custom_role === 'AI Officer';
  const isTaskOwner = task.user_email === currentUserEmail;
  const isBotOwner = officer?.bot_owner_email === currentUserEmail;
  
  let statusColor = "bg-slate-800/50 border-slate-700";
  let statusLabel = "QUEUED";
  let indicatorColor = "bg-slate-600";
  
  if (task.status_flag === 'green') {
    statusColor = "bg-emerald-950/30 border-emerald-900/50";
    statusLabel = "CONQUERED";
    indicatorColor = "bg-emerald-500";
  } else if (task.status_flag === 'red') {
    statusColor = "bg-rose-950/30 border-rose-900/50";
    statusLabel = "STRUGGLING";
    indicatorColor = "bg-rose-500";
  } else if (task.status_flag === 'yellow') {
    statusColor = "bg-amber-950/30 border-amber-900/50";
    statusLabel = "IMMINENTLY CONQUERING";
    indicatorColor = "bg-amber-500";
  }

  return (
    <motion.div 
      layout
      id={id}
      className={cn(
        "relative rounded border transition-all overflow-hidden cursor-pointer",
        isHighlighted ? "bg-indigo-950/40 border-indigo-500 ring-2 ring-indigo-500/30" :
        isExpanded ? "bg-[#0F172A] border-indigo-500/50" : "bg-[#0B1221] hover:bg-slate-900/50 border-slate-800"
      )}
      onClick={onToggleExpand}
    >
      <div className={cn("absolute left-0 top-0 bottom-0 w-1", indicatorColor)} />
      
      <div className="p-3 pl-4">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={cn("text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm border", statusColor)}>
                {statusLabel}
              </span>
              {task.date && (
                <span className={cn(
                  "text-[9px] font-mono",
                  isPast(new Date(task.date + 'T00:00:00')) && task.status_flag !== 'green' ? "text-rose-400" : "text-slate-500"
                )}>
                  {format(new Date(task.date + 'T00:00:00'), 'MMM d')}
                </span>
              )}
            </div>
            <h4 className="text-sm font-medium text-slate-200 leading-snug line-clamp-2">
              {task.task_description}
            </h4>
          </div>
          
          <div className="shrink-0 flex items-center gap-2">
            {isTaskOwner && !task.completed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-slate-500 hover:text-slate-300 hover:bg-slate-800"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                  <DropdownMenuItem 
                    className="text-xs text-slate-300 hover:bg-slate-800 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.();
                    }}
                  >
                    <Pencil className="w-3 h-3 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-xs text-rose-400 hover:bg-slate-800 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdate({ 
                        archived: true, 
                        archived_at: new Date().toISOString() 
                      });
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Remove
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-xs text-amber-400 hover:bg-slate-800 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowArchiveDialog(true);
                    }}
                  >
                    <Archive className="w-3 h-3 mr-2" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className={cn(
              "w-7 h-7 rounded-full border-2 flex items-center justify-center overflow-hidden",
              task.status_flag === 'red' ? "border-rose-500" :
              task.status_flag === 'yellow' ? "border-amber-500" : "border-slate-700"
            )}>
              {officer?.avatar_url ? (
                <img src={officer.avatar_url} className="w-full h-full object-cover" alt="" />
              ) : isBot ? (
                <Bot className="w-3.5 h-3.5 text-purple-400" />
              ) : (
                <span className="text-[10px] font-bold text-slate-400">
                  {officer?.callsign?.substring(0, 2)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Expanded Controls */}
        <AnimatePresence>
          {isExpanded && canManage && !task.completed && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-slate-800/50 pt-3 mt-3 overflow-hidden"
            >
              <div className="flex gap-2 flex-wrap">
                {task.started_at ? (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-[10px] bg-amber-900/20 border-amber-800/50 text-amber-500 hover:bg-amber-900/40"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdate({ status_flag: 'yellow', started_at: null });
                    }}
                  >
                    <Pause className="w-3 h-3 mr-1" /> Pause
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-[10px] bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdate({ status_flag: 'yellow', started_at: new Date().toISOString() });
                    }}
                  >
                    <Play className="w-3 h-3 mr-1" /> Start
                  </Button>
                )}
                
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-[10px] bg-emerald-900/20 border-emerald-800/50 text-emerald-500 hover:bg-emerald-900/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdate({ 
                      completed: true, 
                      status_flag: 'green',
                      completed_at: new Date().toISOString() 
                    });
                  }}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px] bg-rose-900/10 border-rose-900/30 text-rose-500 hover:bg-rose-900/30"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReportIssue?.();
                  }}
                >
                  <AlertOctagon className="w-3 h-3 mr-1" /> Blocked
                </Button>

                {/* Bot Run Button */}
                {isBot && (isBotOwner || canManage) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] bg-purple-900/20 border-purple-800/50 text-purple-400 hover:bg-purple-900/40"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRunBotTask?.(task);
                    }}
                  >
                    <Bot className="w-3 h-3 mr-1" /> {task.bot_execution_status === 'completed' ? 'Re-run' : 'Run'} Bot
                  </Button>
                )}

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        onConfirm={() => {
          onUpdate({
            archived: true,
            archived_at: new Date().toISOString()
          });
          setShowArchiveDialog(false);
        }}
        itemType="task"
        itemTitle={task.task_description}
      />
    </motion.div>
  );
}